import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useUGGApiVersions } from "../ugg-api-versions";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { getApiRoot } from "@ugg/shared/api/api-helpers";
import { cleanOverviewData } from "@ugg/shared/api/data-parser/champions/overview";
import { champion_transform_map, MultipleChampionOverviewData } from "@ugg/shared/utils/multi-build-helpers";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";

import { Overview, Multibuilds } from "@ugg/shared/interfaces/champions/overview.interface";
import { OverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";
import { getKey } from "@ugg/shared/api/data-parser/champions/common";
import {
  OVERVIEW_EMERALD_PLUS_WORLD,
  OVERVIEW_EMERALD_PLUS_WORLD_AP,
  OVERVIEW_EMERALD_PLUS_WORLD_CT,
} from "@ugg/shared/constants/constants";
import { RankS } from "@ugg/shared/utils/rank-helpers";
import { RegionS } from "@ugg/shared/utils/region-helpers";

import { cleanRankingsData, RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";
import { ALL_TIERS, ARAM_MAYHEM_TIER_TO_STDEVS } from "@ugg/shared/utils/tier-helpers";
import { RankingStats } from "@ugg/shared/interfaces/champions/ranking-stats.interface";

interface ChampionOverviewOption {
  queueType?: string;
  patch?: string;
  opp?: number;
}

// Shape of the ARAM Mayhem champion tier list JSON on the CDN
// (mirrors AramMayhemChampionTierList over in the tier-list
// container module). Duplicated here rather than imported to avoid
// coupling this backend module to the frontend feature package —
// the shape is small and hasn't churned since the data pipeline
// was written.
interface AramMayhemChampionTierListJson {
  tiers: Record<"S+" | "S" | "A" | "B" | "C" | "D", number[]>;
  lastUpdated: string;
}

export function skipChampionQuery(championId: number | undefined | null) {
  return !championId || championId < 0;
}

function useChampionOverviewURL(championId: number, options: ChampionOverviewOption) {
  let { queueType, patch, opp } = options;

  const { data: version } = useUGGApiVersions({ apiKey: "overview", patch });

  const hasOpp = opp && (queueType === QueueTypeS.RANKED_SOLO || queueType === QueueTypeS.RANKED_FLEX);

  // backend saves to /urf/ url instead of /arurf/
  if (queueType === QueueTypeS.ARURF) {
    queueType = QueueTypeS.URF;
  }

  const championIdParam = !hasOpp ? championId : `matchups/${championId}_${opp}`;
  const root = getApiRoot(patch);

  return [`${root}/overview`, patch, queueType, championIdParam, `${version}.json`].join("/");
}

function useCTChampionOverviewURL(championId: number, options: ChampionOverviewOption) {
  const { data: version } = useUGGApiVersions({ apiKey: "overview", patch: options.patch });

  if (options.queueType !== (QueueTypeS.RANKED_SOLO || QueueTypeS.RANKED_FLEX)) {
    delete options.opp;
  }

  // backend saves to /urf/ url instead of /arurf/
  if (options.queueType === QueueTypeS.ARURF) {
    options.queueType = QueueTypeS.PICK_URF;
  }

  const championIdParam = !options.opp ? championId : `matchups/${championId}_${options.opp}`;
  const root = getApiRoot(options.patch);

  const path = [`${root}/ct-overview`, options.patch, options.queueType, championIdParam, `${version}.json`].join("/");
  return path;
}

// Champion Multi-Build Overview
export function useSelectedChampionOverviewURL(championId: number, options: ChampionOverviewOption, selector: Multibuilds) {
  const { data: version } = useUGGApiVersions({ apiKey: "overview", patch: options.patch });

  if (options.queueType !== QueueTypeS.RANKED_SOLO && options.queueType !== QueueTypeS.RANKED_FLEX) {
    delete options.opp;
  }

  // backend saves to /urf/ url instead of /arurf/
  if (options.queueType === QueueTypeS.ARURF) {
    options.queueType = QueueTypeS.URF;
  }

  const championIdParam = !options.opp ? championId : `matchups/${championId}_${options.opp}`;
  const root = getApiRoot(options.patch);

  const path = [
    `${root}/${selector !== Multibuilds.RECOMMENDED ? [selector, "overview"].join("-") : "overview"}`,
    options.patch,
    options.queueType,
    championIdParam,
    `${version}.json`,
  ].join("/");
  //console.log(path)
  return path;
}

export function useSelectedChampionOverview(
  championId: number,
  selector: Multibuilds,
  options?: { params?: Record<string, string>; ssr?: boolean; skip?: boolean },
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const { getChampionIdByName } = getRiotAssetsContext();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);

  const championOverviewURL = useSelectedChampionOverviewURL(
    championId,
    {
      ...validatedQueryParams,
      opp: getChampionIdByName(validatedQueryParams.opp),
    },
    selector,
  );

  const customCacheKey = `${OVERVIEW_EMERALD_PLUS_WORLD}_${selector}::${championOverviewURL}`;
  return useJSONFetcher(championOverviewURL, {
    ssr,
    skip: skip,
    customCacheKey:
      validatedQueryParams.region === RegionS.WORLD &&
      validatedQueryParams.rank === RankS.EMERALD_PLUS &&
      validatedQueryParams.queueType === QueueTypeS.RANKED_SOLO
        ? customCacheKey
        : "",
    onCompleted: (url, json, cacheKey) => {
      const cleanData = cleanOverviewData(json);

      const dataKey = getKey(validatedQueryParams.region, validatedQueryParams.rank, "");

      if (cacheKey.match(`${OVERVIEW_EMERALD_PLUS_WORLD}_${selector}::${championOverviewURL}`)) {
        return Object.fromEntries(Object.entries(cleanData || {}).filter(([key, value]) => key.match(dataKey)));
      }

      return cleanData;
    },
  });
}

export function useChampionOverview(
  championId: number,
  options?: { params?: Record<string, string>; ssr?: boolean; skip?: boolean },
) {
  const { params = {}, ssr = false, skip = false } = options || {};

  const { getChampionIdByName } = getRiotAssetsContext();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);

  const championOverviewURL = useChampionOverviewURL(championId, {
    ...validatedQueryParams,
    opp: getChampionIdByName(validatedQueryParams.opp),
  });

  const recCacheKey = `${OVERVIEW_EMERALD_PLUS_WORLD}_${Multibuilds.RECOMMENDED}::${championOverviewURL}`;

  const fetchState = useJSONFetcher<Overview, ReturnType<typeof cleanOverviewData>>(championOverviewURL, {
    ssr,
    skip,
    onCompleted: (url, json, cacheKey) => {
      const cleanData = cleanOverviewData(json);

      const dataKey = getKey(validatedQueryParams.region, validatedQueryParams.rank, "");

      if (cacheKey.match(recCacheKey)) {
        return Object.fromEntries(Object.entries(cleanData || {}).filter(([key, value]) => key.match(dataKey)));
      }

      return cleanData;
    },
    customCacheKey:
      validatedQueryParams.region === RegionS.WORLD &&
      validatedQueryParams.rank === RankS.EMERALD_PLUS &&
      validatedQueryParams.queueType === QueueTypeS.RANKED_SOLO
        ? recCacheKey
        : "",
  });

  return { ...fetchState, params: validatedQueryParams };
}

export function useMultipleChampionOverview(
  championId: number,
  options?: { params?: Record<string, string>; ssr?: boolean; skip?: boolean },
) {
  const { params = {}, ssr = false, skip = false } = options || {};

  const res: Record<number, MultipleChampionOverviewData> = {};
  const maxBuilds = Object.values(champion_transform_map).reduce((acc, curr) => {
    return Math.max(curr.length, acc);
  }, 1);
  const baseArray = Array(maxBuilds).fill(0);
  const champions = champion_transform_map[championId]
    ? champion_transform_map[championId].concat(baseArray.slice(champion_transform_map[championId].length))
    : [championId].concat(baseArray.slice(1));
  let otherChampOverviews: {
    [key: number]: {
      [key: string]: {
        targetChampionOverview: Record<string, OverviewInfo>;
        fetchingTargetChampionOverview: boolean;
        errorTargetChampionOverview: Error | null;
      };
    };
  } = {};

  champions.forEach((championId) => {
    //Get champion transform from new location
    let {
      data: championOverview,
      loading: fetchingChampionOverview,
      error: errorChampionOverview,
    } = championId > 10000
      ? useCTChampionOverview(championId, { ssr, params, skip: skip || skipChampionQuery(championId) })
      : useChampionOverview(championId, { ssr, params, skip: skip || skipChampionQuery(championId) });

    [Multibuilds.AP, Multibuilds.TANK, Multibuilds.AD, Multibuilds.CRIT, Multibuilds.LETHALITY, Multibuilds.ONHIT].forEach(
      (val) => {
        let {
          data: targetChampionOverview,
          loading: fetchingTargetChampionOverview,
          error: errorTargetChampionOverview,
        } = useSelectedChampionOverview(championId % 10000, val, {
          params,
          ssr,
          skip: skip || skipChampionQuery(championId),
        });
        if (!otherChampOverviews[championId]) {
          otherChampOverviews[championId] = {
            [val]: {
              targetChampionOverview: targetChampionOverview,
              fetchingTargetChampionOverview: fetchingTargetChampionOverview,
              errorTargetChampionOverview: errorTargetChampionOverview,
            },
          };
        } else {
          otherChampOverviews[championId][val] = {
            targetChampionOverview: targetChampionOverview,
            fetchingTargetChampionOverview: fetchingTargetChampionOverview,
            errorTargetChampionOverview: errorTargetChampionOverview,
          };
        }
      },
    );

    const forcedDefaultParams = { ...params, opp: "allChampions" };
    const forceDefaultData = !(params.opp && championOverview && !fetchingChampionOverview);
    const {
      data: defaultChampionOverview,
      loading: fetchingDefaultChampionOverview,
      error: errorDefaultChampionOverview,
    } = championId > 10000
      ? useCTChampionOverview(championId, { ssr, params: forcedDefaultParams, skip: !forceDefaultData })
      : useChampionOverview(championId, {
          ssr,
          params: forcedDefaultParams,
          skip: !forceDefaultData || skipChampionQuery(championId),
        });

    if (forceDefaultData) {
      championOverview = defaultChampionOverview;
      fetchingChampionOverview = fetchingDefaultChampionOverview;
      errorChampionOverview = errorDefaultChampionOverview;
    }

    const errorChampOverview = errorChampionOverview
      ? Error("Champion Overview fetch Failed.")
      : false ||
        Object.keys(otherChampOverviews[championId])
          .map((val: string) => {
            return otherChampOverviews[championId][val]["errorTargetChampionOverview"];
          })
          .filter((val) => val)?.[0];

    const fetchingChampOverview =
      fetchingChampionOverview ||
      Object.keys(otherChampOverviews[championId])
        .map((val: string) => {
          return otherChampOverviews[championId][val]["fetchingTargetChampionOverview"];
        })
        .some((val) => val);

    res[championId] = {
      forceDefaultData: forceDefaultData,
      championOverview: championOverview,
      apChampionOverview: otherChampOverviews[championId]["ap"]["targetChampionOverview"],
      tankChampionOverview: otherChampOverviews[championId]["tank"]["targetChampionOverview"],
      adChampionOverview: otherChampOverviews[championId]["ad"]["targetChampionOverview"],
      critChampionOverview: otherChampOverviews[championId]["crit"]["targetChampionOverview"],
      lethalityChampionOverview: otherChampOverviews[championId]["lethality"]["targetChampionOverview"],
      onHitChampionOverview: otherChampOverviews[championId]["onhit"]["targetChampionOverview"],
      fetchingChampionOverview: fetchingChampOverview,
      errorChampionOverview: errorChampOverview,
    };
  });

  return res;
}

export function useCTChampionOverview(
  championId: number,
  { params = {}, ssr, skip }: { params?: Record<string, string>; ssr?: boolean; skip?: boolean } = {},
) {
  const { getChampionIdByName } = getRiotAssetsContext();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);

  const championOverviewURL = useCTChampionOverviewURL(championId, {
    ...validatedQueryParams,
    opp: getChampionIdByName(validatedQueryParams.opp),
  });

  return useJSONFetcher(championOverviewURL, {
    ssr,
    skip,
    customCacheKey: getCustomCacheKey(validatedQueryParams, `${OVERVIEW_EMERALD_PLUS_WORLD_CT}::${championOverviewURL}`),
    onCompleted: (_url, json, cacheKey) => {
      return processOnCompleted(json, cacheKey, OVERVIEW_EMERALD_PLUS_WORLD_CT, validatedQueryParams);
    },
  });
}

function getCustomCacheKey(params: { [key: string]: string }, customCacheKey: string) {
  return params.region === RegionS.WORLD && params.rank === RankS.EMERALD_PLUS && params.queueType === QueueTypeS.RANKED_SOLO
    ? customCacheKey
    : "";
}

function processOnCompleted(json: Overview, cacheKey: string, cacheKeyMatch: string, params: { [key: string]: string }) {
  const cleanData = cleanOverviewData(json);

  const dataKey = getKey(params.region, params.rank, "");

  if (cacheKey.match(cacheKeyMatch)) {
    return Object.fromEntries(Object.entries(cleanData || {}).filter(([key, _value]) => key.match(dataKey)));
  }
  return cleanData;
}

// Shape of the ARAM Mayhem champion tier list JSON on the CDN
// (mirrors AramMayhemChampionTierList over in the tier-list
// container module). Duplicated here rather than imported to avoid
// coupling this backend module to the frontend feature package —
// the shape is small and hasn't churned since the data pipeline
// was written.
interface AramMayhemChampionTierListJson {
  tiers: Record<"S+" | "S" | "A" | "B" | "C" | "D", number[]>;
  lastUpdated: string;
}

// Builds the CLEANED ranking-stats shape for a single champion in
// ARAM Mayhem — emits `Record<string, RankingStatsInfo>` with a
// single entry at the same composite key `getRankings` will look
// up, matching what `cleanRankingsData` would produce for a normal
// fetch. Only fields ChampionProfileHeader actually reads (via
// getRankings) get non-zero values:
//   • stdevs — reverse-mapped from the tier string
//   • rank   — 1 when the champion is in a tier, 0 otherwise, so
//              the header's `stdevs && rank` truthy check works
// Everything else is 0 / empty / "0.0" — ARAM Mayhem has no
// per-role win rates, bans, damage-per-game, counters, etc., and
// none of it is surfaced by any consumer of this hook today.
//
// The reason we build the CLEANED shape directly rather than
// routing through cleanRankingsData:
//   1. cleanRankingsData expects `Record<RegionN, Record<RankN,
//      Record<RoleN, ChampionStats>>>` with NUMERIC enum keys at
//      each nesting level (Object.keys → .map(Number)) — string
//      keys like "world" become NaN and the whole loop iterates
//      over [NaN] and produces {}.
//   2. Even if the keys typed correctly, the derived fields
//      (`avg_damage: damage / roleMatches`, `pick_rate:
//      (roleMatches / total_matches) * 100`, ...) would all
//      divide by 0 and land in the RankingStatsInfo as NaN /
//      Infinity. The header doesn't read those, but shipping
//      NaN in shared data is a footgun waiting for the next
//      consumer.

//--------------------------------------------------------------------------------
// Drew(TODO): We want to change unknown to 'AramMayhemChampionTierListJson' to ensure we can
// keep json format and code synced via TS, but we'll need to properly do this in the next iteration.
//--------------------------------------------------------------------------------

function buildAramMayhemRankingsData(
  json: unknown,
  championId: number,
  validatedQueryParams: Record<string, any>,
): Record<string, RankingStatsInfo> {
  const tiers =
    (json as AramMayhemChampionTierListJson | null | undefined)?.tiers ?? ({} as AramMayhemChampionTierListJson["tiers"]);

  let matchedTier: (typeof ALL_TIERS)[number] | undefined;
  for (const tier of ALL_TIERS) {
    const bucket = tiers[tier];
    if (Array.isArray(bucket) && bucket.includes(championId)) {
      matchedTier = tier;
      break;
    }
  }

  // Missing champion → stdevs=0, rank=0. Both fall through the
  // header's `!stdevs || !rank` guard to the neutral #414165
  // badge, matching how the header handles truly missing data.
  const stdevs = matchedTier !== undefined ? ARAM_MAYHEM_TIER_TO_STDEVS[matchedTier] : 0;
  const rank = matchedTier !== undefined ? 1 : 0;

  const info: RankingStatsInfo = {
    roleWins: 0,
    roleMatches: 0,
    rank, // synthetic — passes the header's truthy check when the champion has a tier
    total_rank: 0,
    avg_damage: 0,
    avg_gold: 0,
    avg_kda: "0.0", // string, not number — see the calculateKDA callsite in cleanRankingsData
    avg_cs: 0,
    win_rate: 0,
    pick_rate: 0,
    ban_rate: 0,
    bans: 0,
    total_matches: 0,
    counters: [],
    real_matches: 0,
    stdevs, // synthetic — the whole point of this module
    effective_winrate: 0,
    distribution_count: 0,
    distribution_mean: 0,
    distribution_stdevs: 0,
    be_all_picks: 0,
  };

  // Match getData's ARAM Mayhem normalization (see common.ts:
  // getData force-sets rank=RankS.OVERALL, role=RoleS.NONE for
  // ARAM_MAYHEM regardless of caller params, so the composite key
  // it looks up is always the normalized form). Our synthetic
  // entry has to live at that same key or the lookup misses.
  //
  // Can't read these off validatedQueryParams reliably — the
  // hook's validateQueryParams pass strips `role` (roles aren't a
  // query param on the OVERVIEW page, they come from
  // ChampionProfileContext), so validatedQueryParams.role comes
  // back undefined even when the caller passes role: "none" in
  // params. Hard-coding the two normalized string values matches
  // getData exactly; if those constants ever drift, common.ts
  // and this file need to change in lockstep.
  const key = getKey(
    validatedQueryParams.region as string,
    "overall", // RankS.OVERALL — getData forces this for ARAM Mayhem
    "none", // RoleS.NONE   — getData forces this for ARAM Mayhem
  );

  return { [key]: info };
}

// ARAM Mayhem-specific ranking-stats hook. Same signature as
// useChampionRankingStatsStandard so the top-level dispatcher can
// hand off transparently — same options, same return shape.
//
// Fetch source: the same tier-list JSON useAramMayhemChampionTierList
// consumes on the tier-list page. JSONFetcher's underlying HTTP
// request is deduped by URL at the network level even when cache
// keys differ, so navigating between champion profiles or between
// the tier-list page and a champion profile stays near-zero cost
// after the first fetch.
export function useChampionRankingStatsAramMayhem(
  championId: number,
  options?: { params?: Record<string, any> } & JSONFetcherOptions<RankingStats, ReturnType<typeof cleanRankingsData>>,
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);
  const { patch } = validatedQueryParams;

  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-champions-${patch}.json`;

  // championId is spliced into the cache key because the URL is
  // SHARED across every champion in a given patch (unlike the
  // normal ranking-stats URL, which encodes championId in the
  // path). Without it, whichever champion loaded first wins the
  // cache slot and every subsequent champion pulls back that first
  // champion's derived data — surfaces as "every champion shows
  // the same tier." Per-champion cache slots do cost a small
  // amount of JS memory (one RankingStatsInfo object per visited
  // champion), but the underlying HTTP request is deduped by the
  // browser's own network cache, so the wall-clock cost stays
  // near-zero after the first hit.
  //
  // The RANKING_STATS_ARAM_MAYHEM prefix also namespaces this
  // cache slot away from useAramMayhemChampionTierList, which
  // fetches the same URL but transforms the payload with a
  // different cleaner ({tierlist, lastUpdated} instead of the
  // flat RankingStatsInfo dict). Without the prefix, both hooks
  // would fight over the same slot and whichever wrote last
  // wins — surfacing as a shape mismatch in getRankings
  // downstream.
  const cacheKey = `RANKING_STATS_ARAM_MAYHEM::${championId}::${url}`;

  // Skip the fetch when we don't have a patch — the URL would be
  // malformed and the CDN would 404. Falls through to the
  // caller-supplied `skip` so existing skip behavior is preserved.
  const effectiveSkip = skip || !patch;

  return useJSONFetcher<RankingStats, ReturnType<typeof cleanRankingsData>>(url, {
    ssr,
    skip: effectiveSkip,
    onCompleted: (url, json, _cacheKey) => {
      // Emit the cleaned shape DIRECTLY — we can't route through
      // cleanRankingsData because (1) it expects numeric enum
      // keys at each nesting level, (2) every derived field
      // would divide by roleMatches=0 and produce NaN. See the
      // buildAramMayhemRankingsData docstring for the full story.
      return buildAramMayhemRankingsData(json, championId, validatedQueryParams);
    },
    customCacheKey: cacheKey,
  });
}
